import type { MatchEvent } from "../store/useMatchLogStore";
import { DEFAULT_TIMEOUT_MS, LONG_TIMEOUT_MS } from "./httpTimeouts";

const buildApiBase = (raw?: string) => {
  const sanitized = (raw || "http://localhost:8000").replace(/\/+$/, "");
  return sanitized.endsWith("/api/v1") ? sanitized : `${sanitized}/api/v1`;
};

export const IS_E2E_TEST_MODE = import.meta.env.VITE_E2E_TEST_MODE === "true";
const E2E_BYPASS_TOKEN =
  import.meta.env.VITE_E2E_BYPASS_TOKEN ?? "e2e-playwright";

export const API_URL = buildApiBase(import.meta.env.VITE_API_URL);
export const LOGGER_API_URL = `${API_URL}/logger`;
export const EVENTS_API_URL = `${API_URL}/events`;

const normalizeHeaders = (headers?: HeadersInit): Record<string, string> => {
  if (!headers) return {};
  if (headers instanceof Headers) {
    const acc: Record<string, string> = {};
    headers.forEach((value, key) => {
      acc[key] = value;
    });
    return acc;
  }
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }
  return headers;
};

import { auth } from "./firebase";

/**
 * EPIC-69 / PMA-ENTITY-020: this module has no axios underneath it — every
 * export below (17 of the 18 that issue a network call; this function is
 * the 18th) routes through here, and until this fix it used a bare
 * `fetch()` with no `AbortController`/`AbortSignal`/timeout of any kind.
 * An axios `timeout` on `api.ts`'s client cannot reach this file, so a
 * stalled request on the cockpit's fetch-based surface (match status/clock,
 * events, AI insights, media uploads) never rejected either — the same
 * defect as `api.ts`, same fix mechanism (`AbortSignal.timeout`, the fetch
 * equivalent of axios's `timeout`), applied here as its own mechanism per
 * the one-policy-two-mechanisms decision in `httpTimeouts.ts`.
 *
 * `timeoutMs` defaults to `DEFAULT_TIMEOUT_MS`; callers on a path verified
 * to need more (uploads, AI generation — see `httpTimeouts.ts`) pass
 * `LONG_TIMEOUT_MS` explicitly. If `init.signal` is already set, it wins —
 * no caller does this today, but it keeps this helper honest for any
 * caller that later needs to cancel a request itself (e.g. on unmount).
 */
export const fetchLoggerWithAuth = async (
  url: string,
  init?: RequestInit,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
) => {
  let token = "";

  if (IS_E2E_TEST_MODE) {
    token = E2E_BYPASS_TOKEN;
  } else {
    const user = auth.currentUser;
    if (user) {
      try {
        token = await user.getIdToken();
      } catch (e) {
        console.error("[loggerApi] token error", e);
      }
    }
  }

  const mergedHeaders = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...normalizeHeaders(init?.headers),
  };

  return fetch(url, {
    ...init,
    headers: mergedHeaders,
    signal: init?.signal ?? AbortSignal.timeout(timeoutMs),
  });
};

export interface MatchEventsPage {
  items: MatchEvent[];
  total: number;
  page: number;
  page_size: number;
  has_next: boolean;
  next_page?: number | null;
}

/**
 * EPIC-69 criterion 8: this loop's timeout bound is PER-REQUEST,
 * not total-elapsed. Each page inherits `fetchLoggerWithAuth`'s
 * `DEFAULT_TIMEOUT_MS` (this call passes no `timeoutMs`, and a standard
 * page-size-capped read fits the default — see `httpTimeouts.ts`), so a
 * genuine stall — the defect this epic fixes, connection open, no response
 * ever — on any one page now rejects within `DEFAULT_TIMEOUT_MS` instead of
 * hanging forever, and that rejection propagates straight out of this
 * function: there is no try/catch in the loop, so a stalled page is never
 * retried and cannot cascade into `maxPages` consecutive stalls. That
 * bounds "one stalled page" to `DEFAULT_TIMEOUT_MS`, which is what AC8
 * requires.
 *
 * What this does NOT do is bound total elapsed time across the whole
 * fetch. If every one of up to `maxPages` (100) pages is merely slow but
 * not stalled — each individually resolves just under the per-request
 * timeout — total wall time could still approach
 * `maxPages * DEFAULT_TIMEOUT_MS`. No total-elapsed deadline is added here:
 * the spec's own "Out of scope" section excludes retry/deadline policy
 * beyond making a stall unable to hang the load, and a page-size-capped
 * Mongo read (see `httpTimeouts.ts`) fitting `DEFAULT_TIMEOUT_MS` is the
 * same justification used for every other base-tier read in this file.
 */
export const fetchAllMatchEvents = async (
  matchId: string,
  pageSize = 500,
): Promise<MatchEvent[]> => {
  const aggregated: MatchEvent[] = [];
  let currentPage = 1;
  let safetyCounter = 0;
  const maxPages = 100;

  while (safetyCounter < maxPages) {
    safetyCounter += 1;
    const params = new URLSearchParams({
      page: String(currentPage),
      page_size: String(pageSize),
    });
    const response = await fetchLoggerWithAuth(
      `${LOGGER_API_URL}/matches/${matchId}/events?${params.toString()}`,
    );
    if (!response.ok) {
      const errorPayload = await response.text().catch(() => "");
      throw new Error(
        `Failed to fetch events (${response.status} ${response.statusText}): ${errorPayload}`,
      );
    }
    const payload = await response.json();

    if (Array.isArray(payload)) {
      aggregated.splice(0, aggregated.length, ...payload);
      break;
    }

    const pageData = payload as MatchEventsPage;
    aggregated.push(...pageData.items);

    if (!pageData.has_next || pageData.items.length === 0) {
      break;
    }

    currentPage = pageData.next_page ?? currentPage + 1;
  }

  return aggregated;
};

export const updateMatchStatus = async (
  matchId: string,
  status?: string,
  clockOperation?: "start" | "stop" | "reset",
  matchTime?: number,
) => {
  const body: any = { status, clock_operation: clockOperation };
  if (matchTime !== undefined) {
    body.match_time_seconds = matchTime;
  }
  const response = await fetchLoggerWithAuth(
    `${LOGGER_API_URL}/matches/${matchId}/status`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    const errorPayload = await response.text().catch(() => "");
    console.error(
      "Update status failed:",
      response.status,
      response.statusText,
      errorPayload,
    );
    // PMA-COCK-003 v1.182.0: attach the HTTP status, same as `getMatch`
    // below — callers (`useMatchTimer.ts`'s `handleGlobalClockStop`/
    // `handleGlobalClockReset`) need to distinguish a deliberate server
    // refusal (409) from any other thrown error without resorting to a
    // brittle match on `errorPayload`'s text.
    const error = new Error(
      `Failed to update match status: ${errorPayload}`,
    ) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }

  return response.json();
};

export const updateMatch = async (
  matchId: string,
  updates: Record<string, any>,
) => {
  // Use the clock-mode endpoint for clock-related updates
  const response = await fetchLoggerWithAuth(
    `${LOGGER_API_URL}/matches/${matchId}/clock-mode`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    },
  );

  if (!response.ok) {
    const errorPayload = await response.text().catch(() => "");
    console.error(
      "Update match failed:",
      response.status,
      response.statusText,
      errorPayload,
    );
    // PMA-COCK-003 v1.182.0: see the identical comment in
    // `updateMatchStatus` above — this is the endpoint that returns the
    // Penalties 409 (PMA-MATCH-030) `handleGlobalClockStop`/
    // `handleGlobalClockReset` need to detect by status, not by message text.
    const error = new Error(
      `Failed to update match: ${errorPayload}`,
    ) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }

  return response.json();
};

export const resetMatch = async (matchId: string) => {
  const response = await fetchLoggerWithAuth(
    `${LOGGER_API_URL}/matches/${matchId}/reset`,
    {
      method: "POST",
    },
  );

  if (!response.ok) {
    const errorPayload = await response.text().catch(() => "");
    throw new Error(`Failed to reset match: ${errorPayload}`);
  }

  return response.json();
};

export const getMatch = async (matchId: string) => {
  const response = await fetchLoggerWithAuth(
    `${LOGGER_API_URL}/matches/${matchId}`,
  );
  if (!response.ok) {
    const errorPayload = await response.text().catch(() => "");
    // A 404 here means no logger session document exists yet for this
    // match_id — the expected state for a match that has never been opened
    // in the cockpit. That is not a failure, so don't log it as one; any
    // other status (5xx, auth, network) is a real error and still surfaces.
    if (response.status !== 404) {
      console.error(
        "Get match failed:",
        response.status,
        response.statusText,
        errorPayload,
      );
    }
    const error = new Error(
      `Failed to fetch match: ${errorPayload}`,
    ) as Error & {
      status?: number;
    };
    error.status = response.status;
    throw error;
  }
  return response.json();
};

export const updateMatchEvent = async (
  eventId: string,
  updates: Partial<MatchEvent> & { notes?: string | null },
): Promise<MatchEvent> => {
  const response = await fetchLoggerWithAuth(`${EVENTS_API_URL}/${eventId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const errorPayload = await response.text().catch(() => "");
    throw new Error(`Failed to update event: ${errorPayload}`);
  }

  return response.json();
};

export const deleteMatchEvent = async (eventId: string): Promise<void> => {
  const response = await fetchLoggerWithAuth(`${EVENTS_API_URL}/${eventId}`, {
    method: "DELETE",
  });

  if (!response.ok) {
    const errorPayload = await response.text().catch(() => "");
    throw new Error(`Failed to delete event: ${errorPayload}`);
  }
};

/**
 * EPIC-69 / PMA-ENTITY-020: fits DEFAULT_TIMEOUT_MS, no override. Despite
 * the epic's "analytics aggregation" naming, this reads a precomputed
 * `match_analytics` document (two `find_one` calls, no in-request
 * computation) — the aggregation itself runs incrementally on each event
 * write, not here
 * (ProMatchAnalytics-Backend/app/routers/matches_crud.py:1112-1154,
 * docstring: "Return pre-computed advanced metrics for a match").
 */
export const fetchAdvancedMetrics = async (matchId: string): Promise<any> => {
  const response = await fetchLoggerWithAuth(
    `${API_URL}/matches/${matchId}/advanced-metrics`,
  );
  if (!response.ok) {
    const errorPayload = await response.text().catch(() => "");
    throw new Error(`Failed to fetch advanced metrics: ${errorPayload}`);
  }
  return response.json();
};

// ── AI Insights ──────────────────────────────────────────────────────────────

/**
 * Thrown when the backend AI feature gate is closed (503 response).
 * Callers distinguish this from a real network/server error:
 * the panel shows "not configured" rather than a red error state.
 */
export class AiFeatureDisabledError extends Error {
  readonly status = 503;
  constructor() {
    super("AI features are not configured");
    this.name = "AiFeatureDisabledError";
  }
}

/**
 * Thrown when the backend AI endpoint is rate-limited by the provider (429 response).
 * retryAfter is the number of seconds from the Retry-After header, if present.
 */
export class AiRateLimitedError extends Error {
  readonly status = 429;
  readonly retryAfter: number | null;
  constructor(retryAfter: number | null = null) {
    super("AI is rate-limited");
    this.name = "AiRateLimitedError";
    this.retryAfter = retryAfter;
  }
}

export interface AiAnomalyItem {
  description: string;
  confidence: number;
}

export type AiInsightPayload =
  | { narrative: string }
  | { anomalies: AiAnomalyItem[] };

export interface AiInsightDoc {
  match_id: string;
  kind: "narrative" | "anomaly";
  payload: AiInsightPayload;
  provider: string;
  model?: string;
  created_at: string;
}

export interface AiInsightsResponse {
  match_id: string;
  insights: AiInsightDoc[];
}

export interface AiQueryResponse {
  answer: string;
  truncated: boolean;
  note: string | null;
}

/**
 * GET /ai/matches/{matchId}/insights
 * Throws AiFeatureDisabledError on 503 (feature gate closed).
 * Throws Error on any other non-ok response.
 */
export const fetchAiInsights = async (
  matchId: string,
): Promise<AiInsightsResponse> => {
  const response = await fetchLoggerWithAuth(
    `${API_URL}/ai/matches/${matchId}/insights`,
  );
  if (response.status === 503) {
    throw new AiFeatureDisabledError();
  }
  if (!response.ok) {
    const errorPayload = await response.text().catch(() => "");
    throw new Error(`Failed to fetch AI insights: ${errorPayload}`);
  }
  return response.json();
};

export interface NarrativeResponse {
  match_id: string;
  narrative: string;
}

/**
 * POST /ai/matches/{matchId}/narrative
 * Triggers synchronous narrative generation for a match (analyst+).
 * `language` ("en"|"es") is omitted for the default (en); pass the app UI
 * language (i18n.language, normalized) to generate the narrative in Spanish.
 * Throws AiFeatureDisabledError on 503 (narrative toggle off / env gated).
 * Throws AiRateLimitedError on 429 (provider rate limit; retryAfter from Retry-After header).
 * Throws Error on 502 (provider failure) or other non-ok responses.
 */
export const generateNarrative = async (
  matchId: string,
  language?: "en" | "es",
): Promise<NarrativeResponse> => {
  const query = language && language !== "en" ? `?language=${language}` : "";
  // LONG_TIMEOUT_MS: synchronous LLM generation server-side, bounded by the
  // backend's own 30s provider timeout — see httpTimeouts.ts.
  const response = await fetchLoggerWithAuth(
    `${API_URL}/ai/matches/${matchId}/narrative${query}`,
    { method: "POST" },
    LONG_TIMEOUT_MS,
  );
  if (response.status === 503) {
    throw new AiFeatureDisabledError();
  }
  if (response.status === 429) {
    const retryAfterHeader = response.headers.get("Retry-After");
    const retryAfter = retryAfterHeader ? parseInt(retryAfterHeader, 10) : null;
    throw new AiRateLimitedError(
      retryAfter !== null && !isNaN(retryAfter) ? retryAfter : null,
    );
  }
  if (!response.ok) {
    const errorPayload = await response.text().catch(() => "");
    throw new Error(
      `Failed to generate narrative (${response.status}): ${errorPayload}`,
    );
  }
  return response.json();
};

/**
 * POST /ai/query
 * body: { question, match_id }
 * Throws AiFeatureDisabledError on 503.
 * Throws AiRateLimitedError on 429 (provider rate limit; retryAfter from Retry-After header).
 * Throws Error on 502 (provider failure) or other non-ok responses;
 * caller is responsible for showing a non-blocking inline error.
 */
export const postAiQuery = async (
  question: string,
  matchId: string,
): Promise<AiQueryResponse> => {
  // LONG_TIMEOUT_MS: synchronous LLM call server-side — see httpTimeouts.ts.
  const response = await fetchLoggerWithAuth(
    `${API_URL}/ai/query`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ question, match_id: matchId }),
    },
    LONG_TIMEOUT_MS,
  );
  if (response.status === 503) {
    throw new AiFeatureDisabledError();
  }
  if (response.status === 429) {
    const retryAfterHeader = response.headers.get("Retry-After");
    const retryAfter = retryAfterHeader ? parseInt(retryAfterHeader, 10) : null;
    throw new AiRateLimitedError(
      retryAfter !== null && !isNaN(retryAfter) ? retryAfter : null,
    );
  }
  if (!response.ok) {
    const errorPayload = await response.text().catch(() => "");
    throw new Error(
      `Failed to post AI query (${response.status}): ${errorPayload}`,
    );
  }
  return response.json();
};

export interface AiChatResponse {
  match_id: string;
  answer: string;
  truncated: boolean;
  note?: string | null;
}

/**
 * POST /ai/chat
 * body: { question, match_id }
 * Session chat turn: the server keeps short-term conversation memory keyed
 * by (firebase_uid, match_id) with a 4h sliding TTL. The reply language
 * auto-matches the question's language (server-side).
 * Throws AiFeatureDisabledError on 503.
 * Throws AiRateLimitedError on 429 (provider rate limit; retryAfter from Retry-After header).
 * Throws Error on 502 (provider failure) or other non-ok responses;
 * caller is responsible for showing a non-blocking inline error.
 */
export const postAiChat = async (
  question: string,
  matchId: string,
): Promise<AiChatResponse> => {
  // LONG_TIMEOUT_MS: synchronous LLM call server-side — see httpTimeouts.ts.
  const response = await fetchLoggerWithAuth(
    `${API_URL}/ai/chat`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ question, match_id: matchId }),
    },
    LONG_TIMEOUT_MS,
  );
  if (response.status === 503) {
    throw new AiFeatureDisabledError();
  }
  if (response.status === 429) {
    const retryAfterHeader = response.headers.get("Retry-After");
    const retryAfter = retryAfterHeader ? parseInt(retryAfterHeader, 10) : null;
    throw new AiRateLimitedError(
      retryAfter !== null && !isNaN(retryAfter) ? retryAfter : null,
    );
  }
  if (!response.ok) {
    const errorPayload = await response.text().catch(() => "");
    throw new Error(
      `Failed to post AI chat (${response.status}): ${errorPayload}`,
    );
  }
  return response.json();
};

// ── Media uploads (team logo / player photo) ────────────────────────────────

/**
 * Thrown by the media upload/delete calls on any non-ok response.
 * `message` is the backend's `{detail}` string when present, otherwise a
 * fallback derived from the HTTP status.
 */
export class MediaApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "MediaApiError";
    this.status = status;
  }
}

const extractErrorDetail = async (response: Response): Promise<string> => {
  try {
    const data = await response.json();
    if (data && typeof data.detail === "string") return data.detail;
  } catch {
    // body wasn't JSON — fall through to the status-based message
  }
  return response.statusText || `Request failed (${response.status})`;
};

export interface TeamLogoUploadResponse {
  logo_url: string;
}

export interface PlayerPhotoUploadResponse {
  photo_url: string;
}

/**
 * POST /teams/{teamId}/logo — multipart field `file`. SVG only
 * (`image/svg+xml`), sanitized and <=1MB (server-enforced,
 * `MAX_LOGO_SVG_BYTES`). Throws MediaApiError with the backend's {detail}
 * message on 400/403/404.
 */
export const uploadTeamLogo = async (
  teamId: string,
  file: File,
): Promise<TeamLogoUploadResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  // LONG_TIMEOUT_MS: client->backend transfer plus a backend->GCS hop over
  // whatever connection the operator has pitchside — see httpTimeouts.ts.
  const response = await fetchLoggerWithAuth(
    `${API_URL}/teams/${teamId}/logo`,
    {
      method: "POST",
      body: formData,
    },
    LONG_TIMEOUT_MS,
  );
  if (!response.ok) {
    throw new MediaApiError(
      await extractErrorDetail(response),
      response.status,
    );
  }
  return response.json();
};

/** DELETE /teams/{teamId}/logo — clears the logo blob and logo_url. */
export const deleteTeamLogo = async (teamId: string): Promise<void> => {
  const response = await fetchLoggerWithAuth(
    `${API_URL}/teams/${teamId}/logo`,
    {
      method: "DELETE",
    },
  );
  if (!response.ok) {
    throw new MediaApiError(
      await extractErrorDetail(response),
      response.status,
    );
  }
};

/**
 * POST /players/{playerId}/photo — multipart field `file`. JPEG/PNG/WebP,
 * <=5MB (server-enforced, converted to 512x512 JPEG). Throws MediaApiError
 * with the backend's {detail} message on 400/403/404.
 */
export const uploadPlayerPhoto = async (
  playerId: string,
  file: File,
): Promise<PlayerPhotoUploadResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  // LONG_TIMEOUT_MS: client->backend transfer plus a backend->GCS hop over
  // whatever connection the operator has pitchside — see httpTimeouts.ts.
  const response = await fetchLoggerWithAuth(
    `${API_URL}/players/${playerId}/photo`,
    { method: "POST", body: formData },
    LONG_TIMEOUT_MS,
  );
  if (!response.ok) {
    throw new MediaApiError(
      await extractErrorDetail(response),
      response.status,
    );
  }
  return response.json();
};

/** DELETE /players/{playerId}/photo — clears the photo blob and photo_url. */
export const deletePlayerPhoto = async (playerId: string): Promise<void> => {
  const response = await fetchLoggerWithAuth(
    `${API_URL}/players/${playerId}/photo`,
    { method: "DELETE" },
  );
  if (!response.ok) {
    throw new MediaApiError(
      await extractErrorDetail(response),
      response.status,
    );
  }
};

export interface EraseConversationResponse {
  match_id: string;
  erased: boolean;
}

/**
 * DELETE /ai/conversations/{matchId}
 * Erases the caller's chat conversation for this match so the next message
 * starts a fresh session ("Erase conversation, start new one").
 * Throws AiFeatureDisabledError on 503.
 * Throws AiRateLimitedError on 429.
 * Throws Error on other non-ok responses.
 */
export const eraseAiConversation = async (
  matchId: string,
): Promise<EraseConversationResponse> => {
  const response = await fetchLoggerWithAuth(
    `${API_URL}/ai/conversations/${matchId}`,
    { method: "DELETE" },
  );
  if (response.status === 503) {
    throw new AiFeatureDisabledError();
  }
  if (response.status === 429) {
    const retryAfterHeader = response.headers.get("Retry-After");
    const retryAfter = retryAfterHeader ? parseInt(retryAfterHeader, 10) : null;
    throw new AiRateLimitedError(
      retryAfter !== null && !isNaN(retryAfter) ? retryAfter : null,
    );
  }
  if (!response.ok) {
    const errorPayload = await response.text().catch(() => "");
    throw new Error(
      `Failed to erase AI conversation (${response.status}): ${errorPayload}`,
    );
  }
  return response.json();
};
